import { Move } from "../Types";

/**
 * Builds destination candidates for a selected checker from optimal move sets.
 *
 * FIX: previously only considered sets where the selected checker moves FIRST
 * (moveSet[0].from === selectedPoint). This missed valid destinations from sets
 * where ANOTHER checker moves first and the selected checker moves second.
 *
 * In backgammon moves can be made in ANY order within a turn, so if the optimal
 * set is [{A→B, die:4}, {X→C, die:6}] the player can click X first and move to
 * C, then later click A and move to B. Destination C must therefore be shown.
 *
 * The fix: scan ALL moves in every optimal set for the selected checker, not
 * just the first move.
 */
export function getHighlightedMoveSetsForSelectedPoint(
  optimalMoveSets: Move[][],
  selectedPoint: Move["from"]
): Move[][] {
  const bestByDestination = new Map<string, Move[]>();

  for (const moveSet of optimalMoveSets) {
    // Find the first occurrence of the selected checker in this set.
    for (let i = 0; i < moveSet.length; i++) {
      if (moveSet[i].from !== selectedPoint) continue;

      // Build consecutive same-checker prefixes starting at position i.
      // e.g. if checker X chains X→A→B, we generate [X→A] and [X→A, A→B].
      const prefixes: Move[][] = [[moveSet[i]]];
      for (let j = i + 1; j < moveSet.length; j++) {
        if (moveSet[j].from !== moveSet[j - 1].to) break;
        prefixes.push(moveSet.slice(i, j + 1));
      }

      for (const prefix of prefixes) {
        const destination = String(prefix[prefix.length - 1].to);
        const existing = bestByDestination.get(destination);
        // Keep the longest path; for equal length use first-seen (stable).
        if (!existing || prefix.length > existing.length) {
          bestByDestination.set(destination, prefix);
        }
      }

      // Each optimal set contributes at most one starting position for the
      // selected checker — stop after the first match.
      break;
    }
  }

  return Array.from(bestByDestination.values());
}
